import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  areaCreate,
  areaDetail,
  areaList,
  areaUpdate,
  type AreaListItem,
  type AreaStatusFilter,
  type CreateAreaRequest,
  type UpdateAreaRequest,
} from '@/api/domains/organization/areas';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { useUI } from '@/hooks/ui';
import { useSidePanelLoading } from '@/hooks/ui/useSidePanelLoading';
import {
  createAreaSchema,
  type CreateAreaFormData,
} from '@/schemas/organization';
import { SECTIONS } from '@/types/sections';
import { formatToDdMmYyyy, injectValidationErrors, normalizeNumericSelectValue } from '@/utils';


type AreaFormMode = 'create' | 'edit';

export interface AreaFormRecord {
  mode?: AreaFormMode;
  areaId?: number;
}

interface UseAreaFormPanelParams {
  onSubmit?: () => void;
  readOnly?: boolean;
  record?: AreaFormRecord;
}

interface SelectorOption {
  value: string;
  label: string;
}

interface AreaFormMetadata {
  id: string;
  createdDate: string;
  createdBy: string;
  modifiedDate: string;
  modifiedBy: string;
}

export interface UseAreaFormPanelResult {
  codeValue: string;
  nameValue: string;
  parentAreaValue: string;
  parentAreaOptions: SelectorOption[];
  metadata: AreaFormMetadata | null;
  isEditMode: boolean;
  effectiveReadOnly: boolean;
  isSubmitting: boolean;
  isReady: boolean;
  canSave: boolean;
  handleCodeChange: (value: string) => void;
  handleNameChange: (value: string) => void;
  handleParentAreaChange: (value: string | string[]) => void;
  handleSubmit: (event?: FormEvent<HTMLFormElement>) => void;
  errors: ReturnType<typeof useForm<CreateAreaFormData>>['formState']['errors'];
  isSubmitted: boolean;
}

const DUPLICATE_CODE_ERROR_KEY = 'organization:validation.codeUnique';
const DUPLICATE_NAME_PARENT_ERROR_KEY = 'organization:validation.nameParentUnique';
const MAX_PAGE_SIZE = 200;

const normalizeText = (value: string): string => value.trim().toLocaleLowerCase();

const isManualError = (errorMessage: string | undefined, expectedKey: string): boolean => (
  typeof errorMessage === 'string' && errorMessage === expectedKey
);

export function useAreaFormPanel(
  { onSubmit, readOnly, record }: UseAreaFormPanelParams,
): UseAreaFormPanelResult {
  const { t: translate } = useTranslation();
  const { canSection } = usePagePermissions();
  const { closeSidePanel, showNotification, showApiError } = useUI();
  const { setLoading } = useSidePanelLoading();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [allAreas, setAllAreas] = useState<AreaListItem[]>([]);
  const [metadata, setMetadata] = useState<AreaFormMetadata | null>(null);

  const latestRequestIdRef = useRef(0);
  const areaId = record?.areaId;
  const mode = record?.mode ?? (areaId ? 'edit' : 'create');
  const isEditMode = mode === 'edit';
  const effectiveReadOnly = !!readOnly;
  const canSave = isEditMode
    ? canSection(SECTIONS.AREAS, 'edit')
    : canSection(SECTIONS.AREAS, 'create');

  const {
    handleSubmit: rhfHandleSubmit,
    register,
    unregister,
    setValue,
    watch,
    setError,
    clearErrors,
    getFieldState,
    formState,
  } = useForm<CreateAreaFormData>({
    resolver: zodResolver(createAreaSchema),
    mode: 'onChange',
    defaultValues: {
      code: '',
      name: '',
      parentAreaId: null,
    },
  });

  const codeValue = watch('code') || '';
  const nameValue = watch('name') || '';
  const parentAreaIdValue = watch('parentAreaId');

  const parentAreaValue = typeof parentAreaIdValue === 'number'
    ? String(parentAreaIdValue)
    : '';

  const fetchAllAreas = useCallback(
    async (status: AreaStatusFilter): Promise<AreaListItem[] | null> => {
      let currentPage = 1;
      let totalPages = 1;
      const collectedRows: AreaListItem[] = [];

      do {
        const response = await areaList.list({
          page: currentPage,
          pageSize: MAX_PAGE_SIZE,
          status,
        });

        if (!response.success || !response.data) {
          return null;
        }

        collectedRows.push(...response.data.items);
        totalPages = response.data.totalPages;
        currentPage += 1;
      } while (currentPage <= totalPages);

      return collectedRows;
    },
    [],
  );

  useEffect(() => {
    setLoading(isBootstrapping);
  }, [isBootstrapping, setLoading]);

  useEffect(() => {
    register('code');
    register('name');
    register('parentAreaId');

    return () => {
      unregister('code');
      unregister('name');
      unregister('parentAreaId');
    };
  }, [register, unregister]);

  const loadInitialData = useCallback(async () => {
    if (isEditMode && !areaId) {
      setIsBootstrapping(false);
      showNotification('error', translate('organization:messages.areaLoadError'), 5000);
      closeSidePanel();
      return;
    }

    setIsBootstrapping(true);
    const requestId = ++latestRequestIdRef.current;

    const areasResponse = await fetchAllAreas('all');

    if (requestId !== latestRequestIdRef.current) {
      return;
    }

    if (!areasResponse) {
      setIsBootstrapping(false);
      showNotification('error', translate('organization:messages.areasLoadError'), 5000);
      return;
    }

    setAllAreas(areasResponse);

    if (!isEditMode || !areaId) {
      setIsBootstrapping(false);
      return;
    }

    const detailResponse = await areaDetail.getById(areaId);

    if (requestId !== latestRequestIdRef.current) {
      return;
    }

    if (!detailResponse.success || !detailResponse.data) {
      showApiError(detailResponse, () => translate('organization:messages.areaLoadError'));
      setIsBootstrapping(false);
      closeSidePanel();
      return;
    }

    setValue('code', detailResponse.data.code, { shouldValidate: true });
    setValue('name', detailResponse.data.name, { shouldValidate: true });
    if (typeof detailResponse.data.parentAreaId === 'number') {
      setValue('parentAreaId', detailResponse.data.parentAreaId, { shouldValidate: true });
    }

    setMetadata({
      id: String(detailResponse.data.id),
      createdDate: formatToDdMmYyyy(detailResponse.data.createdDate) || '-',
      createdBy: detailResponse.data.createdBy || '-',
      modifiedDate: formatToDdMmYyyy(detailResponse.data.modifiedDate) || '-',
      modifiedBy: detailResponse.data.modifiedBy || '-',
    });

    setIsBootstrapping(false);
  }, [
    areaId,
    closeSidePanel,
    fetchAllAreas,
    isEditMode,
    setValue,
    showApiError,
    showNotification,
    translate,
  ]);

  useEffect(() => {
    void loadInitialData();
  }, [loadInitialData]);

  const parentAreaOptions = useMemo<SelectorOption[]>(() => allAreas
    .filter((area) => area.isEnabled)
    .filter((area) => !isEditMode || area.id !== areaId)
    .map((area) => ({
      value: String(area.id),
      label: area.name,
    })), [allAreas, areaId, isEditMode]);

  useEffect(() => {
    if (allAreas.length === 0) {
      return;
    }

    const comparableAreas = allAreas.filter((area) => !isEditMode || area.id !== areaId);

    const normalizedCode = normalizeText(codeValue);
    const hasDuplicateCode = normalizedCode.length > 0
      && comparableAreas.some((area) => normalizeText(area.code) === normalizedCode);

    if (hasDuplicateCode) {
      setError('code', {
        type: 'manual',
        message: DUPLICATE_CODE_ERROR_KEY,
      });
    } else {
      const currentCodeError = getFieldState('code').error?.message;
      if (isManualError(currentCodeError, DUPLICATE_CODE_ERROR_KEY)) {
        clearErrors('code');
      }
    }

    const normalizedName = normalizeText(nameValue);
    const hasParentSelection = typeof parentAreaIdValue === 'number';

    const hasDuplicateNameAndParent = normalizedName.length > 0
      && hasParentSelection
      && comparableAreas.some((area) => (
        normalizeText(area.name) === normalizedName
        && area.parentAreaId === parentAreaIdValue
      ));

    if (hasDuplicateNameAndParent) {
      setError('name', {
        type: 'manual',
        message: DUPLICATE_NAME_PARENT_ERROR_KEY,
      });
      setError('parentAreaId', {
        type: 'manual',
        message: DUPLICATE_NAME_PARENT_ERROR_KEY,
      });
    } else {
      const currentNameError = getFieldState('name').error?.message;
      if (isManualError(currentNameError, DUPLICATE_NAME_PARENT_ERROR_KEY)) {
        clearErrors('name');
      }

      const currentParentAreaError = getFieldState('parentAreaId').error?.message;
      if (isManualError(currentParentAreaError, DUPLICATE_NAME_PARENT_ERROR_KEY)) {
        clearErrors('parentAreaId');
      }
    }
  }, [
    allAreas,
    areaId,
    clearErrors,
    codeValue,
    getFieldState,
    isEditMode,
    nameValue,
    parentAreaIdValue,
    setError,
  ]);

  const handleCodeChange = useCallback((value: string) => {
    setValue('code', value, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [setValue]);

  const handleNameChange = useCallback((value: string) => {
    setValue('name', value, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [setValue]);

  const handleParentAreaChange = useCallback((value: string | string[]) => {
    const normalized = normalizeNumericSelectValue(value) ?? null;

    setValue('parentAreaId', normalized, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [setValue]);

  const isReady = useMemo(() => {
    const hasCode = codeValue.trim().length > 0;
    const hasName = nameValue.trim().length > 0;
    const hasParentArea = typeof parentAreaIdValue === 'number' && parentAreaIdValue > 0;

    return hasCode && hasName && hasParentArea && !isBootstrapping;
  }, [codeValue, isBootstrapping, nameValue, parentAreaIdValue]);

  const onValid = useCallback(async (data: CreateAreaFormData) => {
    if (effectiveReadOnly || !canSave) {
      return;
    }

    setIsSubmitting(true);

    const requestPayload: CreateAreaRequest | UpdateAreaRequest = {
      code: data.code.trim(),
      name: data.name.trim(),
      parentAreaId: data.parentAreaId ?? undefined,
    };

    const response = isEditMode && areaId
      ? await areaUpdate.update(areaId, requestPayload)
      : await areaCreate.create(requestPayload);

    if (response.success && response.data) {
      showNotification(
        'success',
        translate(
          isEditMode
            ? 'organization:messages.areaUpdated'
            : 'organization:messages.areaCreated',
        ),
        5000,
      );
      window.dispatchEvent(new CustomEvent('area-form-success'));
      onSubmit?.();
      closeSidePanel();
      setIsSubmitting(false);
      return;
    }

    if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
      injectValidationErrors(response.error.validationErrors, { setError });
      setIsSubmitting(false);
      return;
    }

    showApiError(response, translate(
      isEditMode
        ? 'organization:messages.areaUpdateError'
        : 'organization:messages.areaCreateError',
    ));
    setIsSubmitting(false);
  }, [
    areaId,
    canSave,
    closeSidePanel,
    effectiveReadOnly,
    isEditMode,
    onSubmit,
    setError,
    showApiError,
    showNotification,
    translate,
  ]);

  const onInvalid = useCallback(() => {
  }, []);

  const handleSubmit = useCallback((event?: FormEvent<HTMLFormElement>) => {
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
      event.stopPropagation();
    }

    if (isBootstrapping || isSubmitting) {
      return;
    }

    if (effectiveReadOnly) {
      return;
    }

    const wrappedValid = (data: CreateAreaFormData) => { void onValid(data); };
    const wrappedInvalid = () => { onInvalid(); };
    const runner = rhfHandleSubmit(wrappedValid, wrappedInvalid);
    void runner(event);
  }, [
    effectiveReadOnly,
    isBootstrapping,
    isSubmitting,
    onInvalid,
    onValid,
    rhfHandleSubmit,
  ]);

  return {
    codeValue,
    nameValue,
    parentAreaValue,
    parentAreaOptions,
    metadata,
    isEditMode,
    effectiveReadOnly,
    isSubmitting,
    isReady,
    canSave,
    handleCodeChange,
    handleNameChange,
    handleParentAreaChange,
    handleSubmit,
    errors: formState.errors,
    isSubmitted: formState.isSubmitted,
  };
}

export default useAreaFormPanel;
